import { useLocalizedText } from '../../hooks/localizedText';
import { useSearchParams } from 'react-router-dom';
import {
  AmbassadorRegistrationForm,
  AmbassadorRegistrationFormValues,
} from './ambassadorRegistrationForm';
import { featureIsEnabled, useFeatures } from '../../hooks/features';
import { COMMUNITY_AMBASSADOR_FEATURE_FLAG } from '../../utils/constants';
import { useEffect, useRef, useState } from 'react';
import { ambassadorFormValuesToUpdateRequest } from '../../utils/ambassadors';
import { useUpdateAmbassador } from '../../hooks/ambassadors/updateAmbassador';
import { useUser } from '../../hooks/user';
import { useGetAmbassador } from '../../hooks/ambassadors/getAmbassador';
import { Link, Modal } from '@amzn/awsui-components-react';
import { publishKatalMetric } from '../katalAnalytics';

interface UpdateAmbassadorModalProps {
  visible: boolean;
  setVisible: (visible: boolean) => void;
  onSuccess: () => void;
  onError: () => void;
}

export function UpdateAmbassadorModal({
  visible,
  setVisible,
  onSuccess,
  onError,
}: UpdateAmbassadorModalProps) {
  const [searchParams] = useSearchParams();
  const features = useFeatures();

  const [formValues, setFormValues] = useState<AmbassadorRegistrationFormValues>({
    country: null,
    city: null,
    siteCode: null,
    businessUnit: null,
    siteRepresentation: false,
    teamRepresentation: false,
    employeeLedGroupRepresentation: false,
    howDidYouHearSpecification: '',
    howDidYouHear: null,
    myManagerHasApprovedThisRole: false,
    teamLocation: [],
    ergLocation: [],
    ergName: null,
    representedSiteCodes: [],
    representMultipleSites: false,
    sendMeACopy: false,
  });

  const user = useUser();
  const { data: { ambassador } = {} } = useGetAmbassador(user.employeeId || '');

  const { t } = useLocalizedText(['ambassadors', 'translation']);
  const updateAmbassador = useUpdateAmbassador();
  const hasInitialized = useRef(false);

  useEffect(() => {
    if (ambassador && !hasInitialized.current) {
      hasInitialized.current = true;
      setFormValues((fv) => ({
        ...fv,
        siteRepresentation: !!(
          ambassador.representationTypes && ambassador.representationTypes.includes('SITE')
        ),
        teamRepresentation: !!(
          ambassador.representationTypes && ambassador.representationTypes.includes('TEAM')
        ),
        employeeLedGroupRepresentation: !!(
          ambassador.representationTypes && ambassador.representationTypes.includes('ELG')
        ),
        representMultipleSites:
          ambassador.representedSiteCodes?.length && ambassador.representedSiteCodes?.length > 0
            ? true
            : false,
        representedSiteCodes: ambassador.representedSiteCodes
          ? ambassador.representedSiteCodes.map((rsc) => ({ label: rsc, value: rsc }))
          : [],
        teamLocation: ambassador.teamLocation?.length
          ? ambassador.teamLocation.map((value) => ({ value, label: value }))
          : [],
        teamName: ambassador.teamName ?? '',
        ergName: ambassador.ergName?.[0]
          ? { label: ambassador.ergName[0], value: ambassador.ergName[0] }
          : null,
        ergLocation: ambassador.ergLocation?.length
          ? ambassador.ergLocation.map((value) => ({ value, label: value }))
          : [],
      }));
    }
  }, [ambassador, setFormValues]);

  const submitUpdateAmbassador = async () => {
    const updateRequest = ambassadorFormValuesToUpdateRequest(formValues, user.employeeId);
    updateAmbassador.mutate(updateRequest, {
      onSuccess: (data) => {
        publishKatalMetric('update_ambassador_success', JSON.stringify(updateRequest));
        setVisible(false);
        onSuccess();
      },
      onError: (err) => {
        console.error(err);
        publishKatalMetric(
          'update_ambassador_error',
          JSON.stringify({ ...updateRequest, error: err?.message })
        );

        setVisible(false);
        onError();
      },
    });
  };

  if (featureIsEnabled(COMMUNITY_AMBASSADOR_FEATURE_FLAG, features, searchParams)) {
    return (
      <Modal
        onDismiss={() => setVisible(false)}
        visible={visible}
        header={t('ambassadorRegistration.updateRegistration')}
        footer={
          <>
            <p>
              {t('ambassadorRegistration.wantToLeave')}{' '}
              <Link href="/ambassadors/offboarding">
                {t('ambassadorRegistration.unsubscribeHere')}
              </Link>
            </p>
          </>
        }
      >
        <AmbassadorRegistrationForm
          formValues={formValues}
          setFormValues={setFormValues}
          onSubmit={submitUpdateAmbassador}
          loading={updateAmbassador.isPending}
          isUpdateForm
        />
      </Modal>
    );
  }
  return <></>;
}
